import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';

import { catchError, finalize, Observable, shareReplay, switchMap, tap, throwError } from 'rxjs';

import { environment } from '../../environments/environment';
import { mpsEndpoints } from '../mps-endpoints';
import { mpsRouteNames } from '../mps-route-names';
import { MpsOauth2Service } from '../services/mps-oauth2.service';
import { MpsUserService } from '../services/mps-user.service';

@Injectable()
export class MpsUnauthenticatedInterceptor implements HttpInterceptor {
  private oauth2Service = inject(MpsOauth2Service);
  private router = inject(Router);
  // Injected lazily via Injector (not as a field/constructor dependency) to avoid a
  // circular DI chain: MpsUserService -> MpsApiService -> HttpClient -> interceptors.
  private injector = inject(Injector);

  // Shared in-flight refresh so concurrent 401s trigger a single refresh call.
  private refreshInFlight$: Observable<{ token: string }> | null = null;
  // Guard so concurrent/terminal 401s only trigger a single redirect-to-login flow.
  private isRedirecting = false;

  intercept(req: HttpRequest<unknown>, handler: HttpHandler): Observable<HttpEvent<unknown>> {
    return handler.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && req.url !== environment.api.host + '/' + mpsEndpoints.user) {
          const provider = localStorage.getItem('provider');

          if (provider) {
            return this.refreshToken(provider, req, handler);
          } else {
            this.redirectToLogin();
          }
        }

        return throwError(() => error);
      })
    );
  }

  private refreshToken(provider: string, req: HttpRequest<unknown>, handler: HttpHandler) {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.oauth2Service.refreshToken(provider).pipe(
        tap((response) => localStorage.setItem('token', response.token)),
        shareReplay(1),
        finalize(() => {
          this.refreshInFlight$ = null;
        })
      );
    }

    return this.refreshInFlight$.pipe(
      // Retry the request
      switchMap((response) => this.retryRequest(req, response, handler)),
      catchError((error: HttpErrorResponse) => {
        this.redirectToLogin();
        return throwError(() => error);
      })
    );
  }

  private retryRequest(
    req: HttpRequest<unknown>,
    response: { token: string },
    handler: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const clonedRequest = req.clone({
      setHeaders: {
        Authorization: `Bearer ${response.token}`,
      },
    });

    return handler.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.redirectToLogin();
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Terminal "session is dead" path: dedupes concurrent callers so only one
   * redirect flow runs at a time, and lands the user on the login route
   * IMMEDIATELY and UNCONDITIONALLY. This must never depend on an
   * authenticated network call (e.g. providers lookup) since that call is
   * itself likely to fail when the session is dead — the user must never be
   * stranded on a broken page with only a snackbar error.
   *
   * The in-memory user session is cleared BEFORE navigating so that
   * MpsUserService.isLoggedIn() becomes false. Otherwise, in a scenario where
   * another tab already logged out but this tab's in-memory user state is
   * still populated (e.g. after a dead-session 401 here), MpsGuestGuard would
   * see isLoggedIn() === true and bounce the login navigation back to /form,
   * stranding the user with only a snackbar error.
   */
  private redirectToLogin(): void {
    if (this.isRedirecting) {
      return;
    }

    this.isRedirecting = true;

    this.injector.get(MpsUserService).clearSession();

    void this.router.navigateByUrl(mpsRouteNames.auth + '/' + mpsRouteNames.login).finally(() => {
      this.isRedirecting = false;
    });
  }
}
